<script lang="ts">
import { defineComponent, PropType, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalizedNamesStore } from '../../../stores/localizedNames';
import useUnitLogic from '../../../composables/useUnitLogic';
import type { RosterType } from '@interfaces/roster_type';
import type { StructureType } from '@interfaces/structure_type';
import type { UnitType } from '@interfaces/unit_type';

export default defineComponent({
  name: 'CurrentUnits',
  props: {
    roster: {
      type: Object as PropType<RosterType>,
      required: true
    },
    structure: {
      type: Object as PropType<StructureType>,
      required: true
    }
  },
  setup(props) {
    const {
      selected_units,
      available_quantity,
      subtract_unit,
      add_unit
    } = useUnitLogic(props.structure);
    const i18n = useI18n();
    const namesStore = useLocalizedNamesStore();

    // Load names for hero and units
    const loadNames = async () => {
      const items: Array<{ type: 'hero' | 'unit'; tech_name?: string; id?: number }> = [];

      if (props.structure.hero) {
        items.push({
          type: 'hero',
          tech_name: props.structure.hero.tech_name || undefined,
          id: props.structure.hero.id || undefined,
        });
      }

      props.structure.units.forEach(unit => {
        items.push({
          type: 'unit',
          tech_name: unit.tech_name || undefined,
          id: unit.id || undefined,
        });
      });

      await namesStore.ensureNamesLoaded(items, i18n.locale.value);
    };

    onMounted(() => {
      loadNames();
    });

    watch(() => i18n.locale.value, () => {
      loadNames();
    });

    watch(() => props.structure, () => {
      loadNames();
    }, { deep: true });

    const getHeroName = computed(() => {
      if (!props.structure.hero) return null;
      return namesStore.getName(
        'hero',
        props.structure.hero.tech_name || undefined,
        props.structure.hero.id || undefined
      ) || props.structure.hero.name;
    });

    const getUnitName = (unit: UnitType) => {
      return namesStore.getName(
        'unit',
        unit.tech_name || undefined,
        unit.id || undefined
      ) || unit.name;
    };

    return {
      selected_units,
      available_quantity,
      subtract_unit,
      add_unit,
      t: i18n.t,
      getHeroName,
      getUnitName
    };
  },
  computed: {
    units(): UnitType[] {
      return this.structure.hero.available_units;
    }
  },
  methods: {
    handleAddUnit(unit: UnitType): void {
      if(this.available_quantity(unit) > 0) {
        this.add_unit(unit);
      }
    },
    handleSubtractUnit(unit: UnitType): void {
      this.subtract_unit(unit);
    }
  }
});
</script>

<template>
  <div class="roster-current">
    <div class="row no-gutters">
      <div class="col-auto mr-1">
        <div class="selected-hero" @click.prevent="$emit('openUnitModal', structure.hero)">
          <div>
            <div class="hero" v-if="structure.hero">
              <img :src="structure.hero.image_path"
                  v-image-fallback
                  class="image"
                  data-toggle="tooltip"
                  data-placement="bottom"
                  :title="getHeroName" />
            </div>
            <div v-else class="hero">&mdash;</div>
          </div>
          <div class="current">{{ structure.hero?.point_cost }}</div>
        </div>
      </div>
      <div class="col">
        <div class="units">
          <div class="unit-gallery">
            <div
              v-for="unit in structure.units"
              :key="unit.tech_name"
              class="unit shadow-sm"
              >
              <div class="unit-image" @click.prevent="$emit('openUnitModal', unit)">
                <img
                  v-image-fallback
                  :src="unit.image_path"
                  :alt="unit.tech_name"
                  class="image"
                  data-toggle="tooltip"
                  data-placement="bottom"
                  :title="getUnitName(unit)"
                >
              </div>
              <div class="quantity">
                {{ unit.quantity }}
              </div>
              <p class="choose_quantity">
                <button type="button" class="substract" @click.prevent="handleSubtractUnit(unit)">-</button>
                <span class="current">
                  {{ unit.point_cost * unit.quantity}}
                </span>
                <button
                  type="button"
                  class="add"
                  @click.prevent="handleAddUnit(unit)"
                  :disabled="available_quantity(unit) === 0"
                >+</button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="text-divider"><p>{{ t('current_units.add_to_roster') }}</p></div>
  </div>
</template>
